import { CrossTabOutputItem, OutputSelection, OutPutSelectionWithParent } from 'src/app/common/models/crosstab.model';

export class TableOutputProcessorHelper {
    static createSelections(item: CrossTabOutputItem) {
        const baseProps = {
            isDerived: item.isDerived,
            customGroupId: item.customGroupId,
            isCustomGroup: item.isCustomGroup,
            CalculationType: item.CalculationType,
            isCustomCalculation: false
        };

        const nestLevel2Selection = this.createOutputSelection({
            Child: item.Level2ColChild,
            variablecode: item.Level2NestColVariableCode,
            responsecode: item.Level2ColResponseCode,
            ...baseProps
        });

        const nestRowLevel2Selection = this.createOutputSelection({
            Child: item.Level2RowChild,
            variablecode: item.Level2NestRowVariableCode,
            responsecode: item.Level2RowResponseCode,
            ...baseProps
        });

        const nestedRow = this.createOutputSelection({
            Child: item.Level1RowChild,
            variablecode: item.Level1NestRowVariableCode,
            responsecode: item.Level1RowResponseCode,
            NestedArray: item.Level2RowChild ? [nestRowLevel2Selection] : [],
            ...baseProps
        });

        const nestedCol = this.createOutputSelection({
            Child: item.Level1ColChild,
            variablecode: item.Level1NestColVariableCode,
            responsecode: item.Level1ColResponseCode,
            NestedArray: item.Level2ColChild ? [nestLevel2Selection] : [],
            ...baseProps
        });

        const rowSelection = this.createOutputSelection({
            Child: item.rowChild,
            variablecode: item.rowVariableCode,
            responsecode: item.rowResponseCode,
            NestedArray: item.Level1RowChild ? [nestedRow] : [],
            ...baseProps
        });

        const colSelection = this.createOutputSelection({
            Child: item.colChild,
            variablecode: item.colVariableCode,
            responsecode: item.colResponseCode,
            NestedArray: item.Level1ColChild ? [nestedCol] : [],
            ...baseProps
        });

        return {
            row: rowSelection,
            col: colSelection,
            nested: {
                row: { main: nestedRow, level2: nestRowLevel2Selection },
                col: { main: nestedCol, level2: nestLevel2Selection }
            }
        };
    }

    static createOutputSelection(config: any): OutputSelection {
        return {
            Child: config.Child,
            variablecode: config.variablecode,
            responsecode: config.responsecode,
            NestedArray: config.NestedArray || [],
            isDerived: config.isDerived,
            customGroupId: config.customGroupId,
            isCustomGroup: config.isCustomGroup,
            CalculationType: config.CalculationType,
            isCustomCalculation: config.isCustomCalculation || false
        };
    }

    static createParentObjects(item: CrossTabOutputItem, selections: any) {
        const extractParent = (child: string) =>
            child.includes('Custom Group-User Defined') || child.includes('Custom Group - Shared With Me')
                ? child.split('|')[1] : child.split('|')[0];

        return {
            row: {
                Parent: extractParent(item.rowChild),
                Data: [selections.row],
                isCustomGroup: item.isCustomGroup,
                customGroupId: item.customGroupId,
                isCustomCalculation: false
            },
            col: {
                Parent: extractParent(item.colChild),
                Data: [selections.col],
                isCustomGroup: item.isCustomGroup,
                customGroupId: item.customGroupId,
                isCustomCalculation: false
            }
        };
    }

    static processParent(newParent: OutPutSelectionWithParent, parentArray: OutPutSelectionWithParent[], nestedConfig: any) {
        const existingParent = this.findExistingParent(newParent, parentArray);

        if (!existingParent) {
            parentArray.push(newParent);
            return;
        }

        const newSelection = newParent.Data[0];
        const existingChild = this.findExistingChild(newSelection, existingParent);

        if (!existingChild) {
            existingParent.Data.push(newSelection);
            return;
        }

        this.updateNestedArrays(existingChild, newSelection, nestedConfig);
    }

    static findExistingParent(newParent: OutPutSelectionWithParent, parentArray: OutPutSelectionWithParent[]) {
        return parentArray.find(existing => {
            if (newParent.isCustomGroup &&
                (newParent.Parent === 'Custom Group-User Defined' || newParent.Parent === 'Custom Group - Shared With Me')) {
                return existing.Parent === newParent.Parent && existing.customGroupId === newParent.customGroupId;
            }
            return existing.Parent === newParent.Parent;
        });
    }

    static findExistingChild(newSelection: OutputSelection, existingParent: OutPutSelectionWithParent) {
        return existingParent.Data.find(existing => {
            if (existing.Child === newSelection.Child && existing.variablecode === newSelection.variablecode) {
                if (existing.isDerived && existing.CalculationType.toLowerCase() === 'mean') {
                    return true;
                }
                return existing.responsecode === newSelection.responsecode;
            }
            return false;
        });
    }

    static updateNestedArrays(existingChild: OutputSelection, newSelection: OutputSelection, nestedConfig: any) {
        if (!nestedConfig.main?.Child) return;

        const existsInNested = existingChild.NestedArray?.some((existing: any) =>
            newSelection.NestedArray?.some(nested => nested.Child === existing.Child)
        );

        if (!existsInNested && existingChild.NestedArray) {
            existingChild.NestedArray.push(nestedConfig.main);
        }

        if (nestedConfig.level2?.Child && existingChild.NestedArray && existingChild.NestedArray.length > 0) {
            existingChild.NestedArray.forEach((existing: any, index: number) => {
                newSelection.NestedArray?.forEach(nested => {
                    if (existing.Child === nested.Child) {
                        const level2Exists = existing.NestedArray?.some((childItem: any) =>
                            nested.NestedArray?.some(nestedChild =>
                                childItem.Child === nestedChild.Child
                            )
                        );
                        if (!level2Exists && existingChild.NestedArray && existingChild.NestedArray[index].NestedArray) {
                            existingChild.NestedArray[index].NestedArray.push(nestedConfig.level2);
                        }
                    }
                });
            });
        }
    }
}
